import React from 'react'
import {
    HourlyUpdateInvoiceProvider,
    useHourlyUpdateInvoice,
} from './context/HourlyUpdateInvoiceContext'
import Step1 from './Step1'
import Step2 from './Step2'
import Step3 from './Step3'

/**
 * Hourly invoice EDIT wizard.
 *
 * Reached from Case Detail → Edit Invoice, routed by `pages/UpdateInvoiceRouter`
 * when the case bills hourly. The fixed editor (`pages/UpdateInvoice.jsx`) is a
 * sibling and is deliberately untouched.
 *
 * THE SHELL IS THE FIXED EDITOR'S SHELL, COPIED.
 * Same blurred `bg-black/50 backdrop-blur-xl` overlay, same
 * `max-w-[1180px] max-h-[92vh]` card, same close button, same header + bar step
 * indicator, same `max-h-[60vh] overflow-y-auto` body and same footer buttons.
 * Steps 1-2 are that dialog; step 3 breaks out to a full-width review, which is
 * exactly what the fixed editor does — the invoice document needs the width,
 * and by then the case manager is approving a finished invoice rather than
 * filling in a form.
 */

const HEADINGS = {
    1: { title: 'Select Party', subtitle: 'Choose which invoice to update.' },
    2: { title: 'Update Invoice', subtitle: 'Add invoice details and line items.' },
}

const Shell = () => {
    const {
        loading,
        error,
        reload,
        onClose,
        currentStep,
        handleBack,
        handleStep1Continue,
        handleStep2Continue,
        selectedInvoiceIds,
        submitting,
        toast,
    } = useHourlyUpdateInvoice()

    const toastNode = toast ? (
        <div
            className={`fixed bottom-6 right-6 px-4 py-2 rounded-md shadow-lg text-sm z-[70] ${
                toast.type === 'success'
                    ? 'bg-green-600 text-white'
                    : toast.type === 'error'
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-800 text-white'
            }`}
        >
            {toast.message}
        </div>
    ) : null

    // Step 3 leaves the dialog and owns its own full-screen backdrop.
    if (!loading && !error && currentStep === 3) {
        return (
            <>
                <Step3 onExit={onClose} />
                {toastNode}
            </>
        )
    }

    const step = loading || error ? 1 : currentStep
    const heading = HEADINGS[step] || HEADINGS[1]

    return (
        <>
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-xl">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-[1180px] max-h-[92vh] overflow-hidden relative">
                    <button
                        type="button"
                        onClick={onClose}
                        className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 z-50"
                        aria-label="Close"
                    >
                        <svg
                            width="18"
                            height="18"
                            viewBox="0 0 20 20"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                        >
                            <path d="M5 5l10 10M15 5L5 15" />
                        </svg>
                    </button>

                    <div className="px-6 pt-5 pb-3">
                        <h2 className="text-lg font-semibold text-gray-900">
                            {heading.title}
                        </h2>
                        <p className="text-sm text-gray-500 mt-0.5">{heading.subtitle}</p>
                        <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center gap-1.5">
                                {[1, 2, 3].map((n) => (
                                    <div
                                        key={n}
                                        className="h-2 w-8 rounded-full"
                                        style={
                                            step === n
                                                ? { backgroundColor: '#115E59' }
                                                : { backgroundColor: '#E5E7EB' }
                                        }
                                    />
                                ))}
                            </div>
                            <span className="text-sm text-gray-600 whitespace-nowrap">
                                Step {step} of 3
                            </span>
                        </div>
                    </div>

                    {loading || error ? (
                        <div className="px-6 py-16 flex items-center justify-center">
                            {error ? (
                                <div className="flex flex-col items-center text-center gap-3">
                                    <p className="text-sm text-red-600">{error}</p>
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                                        >
                                            Back to Case
                                        </button>
                                        <button
                                            type="button"
                                            onClick={reload}
                                            className="px-3 py-1.5 text-sm font-medium text-white rounded-md"
                                            style={{ backgroundColor: '#115E59' }}
                                        >
                                            Try again
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center gap-3 text-sm text-gray-500">
                                    <span className="inline-block w-4 h-4 rounded-full border-2 border-gray-200 border-t-[#115E59] animate-spin" />
                                    Loading…
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className="px-6 py-3 max-h-[60vh] overflow-y-auto">
                                {currentStep === 1 && <Step1 />}
                                {currentStep === 2 && <Step2 />}
                            </div>

                            <div className="px-6 pb-5 flex items-center justify-between">
                                {currentStep === 2 ? (
                                    <button
                                        type="button"
                                        onClick={handleBack}
                                        className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center gap-1"
                                    >
                                        ← Back
                                    </button>
                                ) : (
                                    <span />
                                )}
                                {currentStep === 1 && (
                                    <button
                                        type="button"
                                        disabled={
                                            selectedInvoiceIds.length === 0 || submitting
                                        }
                                        onClick={handleStep1Continue}
                                        className="ml-auto px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50"
                                        style={{ backgroundColor: '#115E59' }}
                                    >
                                        Continue
                                    </button>
                                )}
                                {currentStep === 2 && (
                                    <button
                                        type="button"
                                        onClick={handleStep2Continue}
                                        className="px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-60"
                                        style={{ backgroundColor: '#115E59' }}
                                    >
                                        Continue to Preview
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
            {toastNode}
        </>
    )
}

export default function HourlyUpdateInvoice({ caseId, onClose, initialContext = null }) {
    return (
        <HourlyUpdateInvoiceProvider
            caseId={caseId}
            onClose={onClose}
            initialContext={initialContext}
        >
            <Shell />
        </HourlyUpdateInvoiceProvider>
    )
}
